import { defaultStudioDraft, sourceKey, studioDraftSchema, type StudioDraft } from './studio-draft';
import type { Ride } from '../domain/ride';
import { browserUuid } from '../domain/browser-crypto';
import { RECOVERY_MESSAGE, recoverDraft, recoverDraftTimeline, legacyRecoveryIssues } from './draft-recovery';
import type { RecoveryIssue } from './draft-recovery-description';
import { initializeOpeningStructures } from './structures-opening-view';

export const STUDIO_DRAFT_PREFIX = 'ride-trace.studio.v1:';
const REUSABLE_KEY = `${STUDIO_DRAFT_PREFIX}preferences`;
export const draftStorageKey = (ride: Ride) => STUDIO_DRAFT_PREFIX + sourceKey(ride);
interface Envelope { version: 1; revision: string; draft: StudioDraft }
export interface LoadedDraft { draft: StudioDraft; saved: string | null; message: string; needsReview: boolean; issues: RecoveryIssue[] }

export function loadStudioDraft(ride: Ride, search: string, configuration?: StudioDraft, preferConfiguration = false): LoadedDraft {
  const defaults = configuration ?? defaultStudioDraft(ride, search);
  let saved: string | null = null;
  try {
    saved = localStorage.getItem(draftStorageKey(ride));
    if (configuration && (preferConfiguration || ride.sourceFingerprint?.startsWith('studio-project:'))) return { draft: configuration, saved, message: '', needsReview: false, issues: [] };
    const raw = saved ?? (configuration ? null : localStorage.getItem(REUSABLE_KEY));
    let result;
    try {
      result = raw !== null ? recoverDraft(raw, defaults, saved === null)
        : { draft: defaults, issues: configuration ? [] : legacyRecoveryIssues() };
    } catch (cause) {
      const reason = cause instanceof Error && cause.message === 'different-recording'
        ? 'The saved composition belongs to a different recording. Default settings for this trace are used instead.'
        : 'Studio cannot read this saved composition. Its editing choices cannot be recovered. Default settings for this trace are used instead.';
      result = { draft: defaults, issues: [{ field: 'composition', label: 'Saved composition', reason,
        savedValue: 'Unavailable: saved settings cannot be recovered', replacementValue: 'Default composition settings',
        removed: 'The previous editing choices will be replaced if you accept these changes.' }] };
    }
    const recovered = recoverDraftTimeline(ride, result);
    const draft = recovered.issues.length ? recovered.draft : initializeOpeningStructures(ride, recovered.draft);
    return { ...recovered, draft, saved, needsReview: recovered.issues.length > 0, message: recovered.issues.length ? RECOVERY_MESSAGE : '' };
  } catch (cause) {
    return { draft: defaults, saved, needsReview: false, issues: [],
      message: `Browser storage is unavailable. Settings remain in this session.${cause instanceof Error ? ` ${cause.message}` : ''}` };
  }
}

/** Compare before writing so an idle tab cannot replace a newer recording draft. */
export function saveStudioDraft(ride: Ride, draft: StudioDraft, expected: string | null): string {
  if (!ride.sourceFingerprint) throw new Error('This recording has no persistent source identity. Settings remain in this session.');
  const key = draftStorageKey(ride);
  if (localStorage.getItem(key) !== expected) throw new Error('Studio settings changed in another tab. Load those settings or keep your current edits.');
  const raw = JSON.stringify({ version: 1, revision: browserUuid(), draft: studioDraftSchema.parse(draft) } satisfies Envelope);
  localStorage.setItem(key, raw);
  const preferences = { ...draft } as Partial<StudioDraft>;
  delete preferences.exportRange; delete preferences.cinematicEnabled; delete preferences.cinematicSequence;
  delete preferences.placeTitle;
  delete preferences.keepSourcePace;
  delete preferences.freshEdit;
  try { localStorage.setItem(REUSABLE_KEY, JSON.stringify({ version: 1, revision: browserUuid(), draft: preferences })); }
  catch { /* The recording draft was saved successfully. */ }
  return raw;
}
